// ShadowBillSection is the same six-value union the server calls
// ActualBillSection ('retail' | 'distribution' | 'metering' | 'environmental' |
// 'other' | 'gst'); this module is imported by both sides.
import type { ShadowBillSection as ActualBillSection } from '../types/billTypes';
import { isSupplyCharge } from './chargeNames';

/**
 * Which section a charge belongs to, by name.
 *
 * Source: the "retailer invoice details to be extracted" sheet (Drive id
 * `1JCHA4yIUPaZrUzJPK_1nMK5nbkukFF_XBHIoJ06IpFE`), columns E and F — the
 * controlled vocabulary of EXPECTED INVOICE ITEM NAME and the EXPECTED INVOICE
 * SECTION each belongs to. Columns B–D of that sheet are each retailer's own
 * bill wording and are NOT paired row-by-row to column E, so only E/F are
 * encoded here. 431 sheet rows reduce to 78 distinct names.
 *
 * Job 67. The parser previously decided sections with a handful of description
 * regexes, one of which ("anything starting with Supply is metering") moved
 * network supply charges out of Network. Erin: "our metering supply charge
 * should always sit in metering, network charges stay in Network".
 *
 * ── Why this is a registry and not fuzzy matching ────────────────────────────
 * Matching is EXACT on the normalised name. No substring, no prefix, no
 * similarity. Job 71 is an open defect about exactly that failure mode —
 * "mapping 1 section of invoice maps anything with a similar name also" — so
 * recall is deliberately traded away for precision. A name we do not recognise
 * keeps whatever section the parse gave it; nothing is guessed.
 *
 * ── What is deliberately NOT here ────────────────────────────────────────────
 * A name is excluded when forcing it could be wrong:
 *
 *  1. It sits in two sections in the sheet, so the name alone cannot decide.
 *     `Supply` is both Metering and Network/Distribution — which is job 67
 *     itself. For these the invoice's own section context wins.
 *  2. It collides with a name our own vocabulary already owns (chargeNames.ts:
 *     RETAIL_ENERGY_NAMES, LEGACY_CHARGE_NAMES, CANONICAL_CHARGE_NAMES). The
 *     sheet lists `Peak Energy` under Network Distribution, but
 *     LEGACY_CHARGE_NAMES maps `Peak Energy` to the RETAIL `Peak`. Forcing it
 *     would move retail energy into the network section.
 *  3. It is a bare time-of-use band plus "usage"/"energy" (`Peak Usage`,
 *     `Off Peak Usage`, `Anytime Usage`, `Shoulder Usage`, `Super Off Peak
 *     Usage`). The sheet lists these as network, but a retailer can print the
 *     same words over retail energy.
 *
 * See AMBIGUOUS_CHARGE_NAMES for the full excluded list.
 *
 * ⚠️ UNRESOLVED, and left out on purpose: the sheet files `AEMO Ancillary` and
 * `AEMO Participant` under Other Charges, but the parser prompt and
 * `reclassifyCertificateLines` both deliberately keep AEMO fees in
 * distribution. Moving them would change distribution totals that feed the
 * variance calculation. Needs a decision before either side changes.
 */

/**
 * Names whose section is unambiguous. Keys are already normalised
 * (`normaliseChargeName`); build via the exported map rather than editing keys
 * by hand.
 */
const REGISTRY_ENTRIES: ReadonlyArray<readonly [string, ActualBillSection]> = [
  // retail (4)
  ['Solar Feed-In Anytime', 'retail'],
  ['Solar Feed-in Off-Peak', 'retail'],
  ['Solar Feed-in Peak', 'retail'],
  ['Solar Feed-in Shoulder', 'retail'],

  // distribution (43)
  ['Additional supply charge', 'distribution'],
  ['Annual Demand Anytime', 'distribution'],
  ['Annual kVA Demand Flexible', 'distribution'],
  ['Annual kVA Demand Peak', 'distribution'],
  ['Anytime Demand', 'distribution'],
  ['Block 1 Usage', 'distribution'],
  ['Block 2 Usage', 'distribution'],
  ['CL Anytime usage', 'distribution'],
  ['CL Off-peak usage', 'distribution'],
  ['CL Peak usage', 'distribution'],
  ['CL Solar sponge usage', 'distribution'],
  ['Category - (batch name)', 'distribution'],
  ['Connection Unit', 'distribution'],
  ['Controlled Load', 'distribution'],
  ['Critical Peak Energy', 'distribution'],
  ['Critical minimum energy', 'distribution'],
  ['Critical peak demand', 'distribution'],
  ['Critical peak export charge', 'distribution'],
  ['Critical peak export rebate', 'distribution'],
  ['DER export rebate', 'distribution'],
  ['Energy Export (charge)', 'distribution'],
  ['Energy Export (reward)', 'distribution'],
  ['Energy Import (charge)', 'distribution'],
  ['Export consumption charge >7.5kWh', 'distribution'],
  ['Export demand charge >1.5KW', 'distribution'],
  ['High Season Peak Demand', 'distribution'],
  ['High Season Peak Usage', 'distribution'],
  ['High Season Shoulder Usage', 'distribution'],
  ['Low Season Peak Demand', 'distribution'],
  ['Low Season Peak Usage', 'distribution'],
  ['Low Season Winter Peak Usage', 'distribution'],
  ['Monthly kVA Demand Peak', 'distribution'],
  ['Net energy consumption', 'distribution'],
  ['Network Specified Capacity', 'distribution'],
  ['Network Supply', 'distribution'],
  ['Off Peak Demand', 'distribution'],
  ['Peak Demand', 'distribution'],
  ['Shoulder Demand', 'distribution'],
  ['Solar Export (charge)', 'distribution'],
  ['Solar Export (reward)', 'distribution'],
  ['Solar Soak', 'distribution'],
  ['Solar Soak Export', 'distribution'],
  ['TUOS Demand', 'distribution'],

  // metering (4)
  ['Meter Rental', 'metering'],
  ['Metering ($/meter/year)', 'metering'],
  ['Supplementery Metering', 'metering'], // sheet's spelling, kept verbatim
  ['Value Added Services', 'metering'],

  // environmental (6)
  ['ESC', 'environmental'],
  ['LREC', 'environmental'],
  ['REPS', 'environmental'],
  ['SREC', 'environmental'],
  ['VEEC', 'environmental'],
  ['VEET', 'environmental'],

  // other (1) — AEMO Ancillary / AEMO Participant are in the sheet under Other
  // but excluded here; see the ⚠️ note above.
  ['Adjustment', 'other'],
] as const;

/**
 * Names the sheet supplies that must NOT be force-filed, with the reason.
 * Exported so a test can assert the registry and this list never overlap, and
 * so the Comparison table can explain why a row was left alone.
 */
export const AMBIGUOUS_CHARGE_NAMES: Readonly<Record<string, string>> = {
  // Listed in two sections by the sheet — the invoice's own section decides.
  'Supply': 'Metering & Additional Charges and Network / Distribution Charges',
  'Supply ($/servicePoint/day)': 'Retail Energy Charges and Metering Charges',
  'Supply ($/servicePoint/month)': 'Retail Energy Charges and Metering Charges',
  'Anytime': 'Retail Energy Charges and Network Distribution Charges',
  // Collides with a name our own vocabulary already owns (chargeNames.ts).
  'Peak': 'retail band name in RETAIL_ENERGY_NAMES',
  'Off Peak': 'retail band name in RETAIL_ENERGY_NAMES',
  'Off-Peak': 'retail band name in RETAIL_ENERGY_NAMES',
  'Shoulder': 'retail band name in RETAIL_ENERGY_NAMES',
  'Peak Energy': 'LEGACY_CHARGE_NAMES maps this to the retail "Peak"',
  'Off Peak Energy': 'LEGACY_CHARGE_NAMES maps this to the retail "Off-Peak"',
  'Shoulder Energy': 'LEGACY_CHARGE_NAMES maps this to the retail "Shoulder"',
  'Metering': 'Master Data metering name, unit-qualified elsewhere',
  'Green Power': 'handled by reclassifyCertificateLines',
  // Bare TOU band + usage/energy: the sheet calls these network, but a retailer
  // can print the same words over retail energy.
  'Peak Usage': 'bare TOU band — could be retail or network',
  'Off Peak Usage': 'bare TOU band — could be retail or network',
  'Anytime Usage': 'bare TOU band — could be retail or network',
  'Shoulder Usage': 'bare TOU band — could be retail or network',
  'Super Off Peak Usage': 'bare TOU band — could be retail or network',
  // Spec conflict, see the ⚠️ note above.
  'AEMO Ancillary': 'sheet says Other; parser prompt says distribution — undecided',
  'AEMO Participant': 'sheet says Other; parser prompt says distribution — undecided',
};

/**
 * Case- and whitespace-insensitive. Nothing else is stripped: punctuation and
 * unit annotations are part of the name, because `Metering ($/meter/year)` and
 * a bare `Metering` are different entries with different answers.
 */
export function normaliseChargeName(description: string): string {
  return description.replace(/\s+/g, ' ').trim().toLowerCase();
}

const BY_NAME: ReadonlyMap<string, ActualBillSection> = new Map(
  REGISTRY_ENTRIES.map(([name, section]) => [normaliseChargeName(name), section]),
);

const AMBIGUOUS_NORMALISED: ReadonlySet<string> = new Set(
  Object.keys(AMBIGUOUS_CHARGE_NAMES).map(normaliseChargeName),
);

/** Every name the registry will force, for tests and admin display. */
export const REGISTERED_CHARGE_NAMES: ReadonlyArray<readonly [string, ActualBillSection]> =
  REGISTRY_ENTRIES;

/**
 * The section this charge always belongs to, or null when it has no fixed home.
 * Null means "leave it where it already is" — never "guess".
 *
 * This is THE definition, used by all three enforcement points (see
 * features/billValidation/AGENTS.md): the shadow calculation's emitted section,
 * `invoiceParserService`'s billed-line pass, and `comparisonRows`' paired-row
 * pass. Two copies of this rule is how a charge ended up in two sections.
 *
 * `currentSection` is where the charge is filed right now — the parse's section
 * for a billed line, the row's section for a paired row. It only matters for
 * names the sheet lists in more than one section:
 *
 *   Job 67. `Supply` is valid in BOTH Metering and Network/Distribution, so the
 *   name alone cannot decide. Previously anything starting "Supply" was forced
 *   to metering, which dragged a network supply charge printed as a bare
 *   "Supply Charge" out of Network. Erin: "our metering supply charge should
 *   always sit in metering, network charges stay in Network". So when the
 *   invoice itself already filed it under distribution, that wins.
 *
 * Called without `currentSection` the supply rule keeps its original behaviour,
 * which is what the non-billed callers want.
 */
export function canonicalSectionFor(
  description: string,
  currentSection: ActualBillSection | null = null,
): ActualBillSection | null {
  // Supply is checked first and by its own predicate: it has to match invoice
  // wording ("Supply Charge (/day)") as well as canonical labels, which an
  // exact-name registry lookup cannot do.
  if (isSupplyCharge(description)) {
    return currentSection === 'distribution' ? null : 'metering';
  }

  const key = normaliseChargeName(description);
  if (AMBIGUOUS_NORMALISED.has(key)) return null;
  return BY_NAME.get(key) ?? null;
}

/** Whether a name is one the sheet lists in more than one section. */
export function isAmbiguousChargeName(description: string): boolean {
  return AMBIGUOUS_NORMALISED.has(normaliseChargeName(description));
}
